import { applyFilters, type FilterValues } from "@/lib/filters";

import type { GridResponse, GridRow, Margins, Market } from "./api";
import { channelLabel, countryName, PAGE_SIZE, rowKey, STORE_LABEL, type SortKey } from "./format";

export type FilterField = "product" | "variant" | "channel" | "store" | "location" | "marketplace";
export const FILTER_FIELDS: FilterField[] = ["product", "variant", "channel", "store", "location", "marketplace"];
const GROUP_FIELDS: FilterField[] = ["product", "variant", "channel", "store"];
const LINE_FIELDS: FilterField[] = ["location", "marketplace"];
export type Filters = Record<FilterField, string | null>;
const NO_FILTERS: Filters = { product: null, variant: null, channel: null, store: null, location: null, marketplace: null };

/**
 * The value a row offers for one filter, or null when the filter does not apply to that grain.
 * Store is a property of the parent row: a ship-country row inherits its store and offers none.
 * Location is the two-letter place; Marketplace is its name (Amazon.de, or Germany for a ship country).
 */
export function fieldOf(row: GridRow, field: FilterField): string | null {
  if (field === "product") return row.product;
  if (field === "variant") return row.variant;
  if (field === "channel") return channelLabel(row);
  if (field === "store") return row.grain === "ship_country" ? null : STORE_LABEL[row.channel_location] ?? row.channel_location;
  if (field === "location") return row.grain === "store" ? null : row.location;
  if (row.grain === "marketplace") return row.marketplace_name;
  return row.grain === "ship_country" ? countryName(row.location) : null;
}

/** A grid's rows as groups, in object order: an Amazon marketplace row with no children, or a Shopify store row with its
 * ship-country rows (the rows that follow it and name it as their parent). */
function groupsOf(rows: GridRow[]): Array<{ row: GridRow; children: GridRow[] }> {
  const out: Array<{ row: GridRow; children: GridRow[] }> = [];
  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i];
    if (row.grain === "ship_country") continue;
    const children: GridRow[] = [];
    if (row.grain === "store") {
      for (let j = i + 1; j < rows.length && rows[j].grain === "ship_country" && rows[j].parent_sku === row.xero_sku; j += 1) children.push(rows[j]);
    }
    out.push({ row, children });
  }
  return out;
}

/**
 * Sorted distinct values present in the loaded rows: the options are never a fixed list. With `filters` (C1 v1.41), only the
 * rows that match every OTHER active filter offer their values, so each dropdown narrows to what the others leave: Product
 * "MCT Oil" leaves "500ml" alone under Variant, Location "CH" leaves the one product that ships there. A group field's value
 * comes from the store or marketplace row of each group that matches; a line field's from the marketplace row or the ship
 * countries that match the other line filter. The field's own picked value stays listed whatever the others leave, so the
 * control always shows what is picked and it can be cleared. Without `filters` every loaded row offers its value, as before.
 */
export function options(grids: GridResponse[], field: FilterField, filters: Partial<Filters> = {}): Array<{ value: string; label: string }> {
  const others: Filters = { ...NO_FILTERS, ...filters, [field]: null };
  const lineFilter = Boolean(others.location || others.marketplace);
  const seen = new Set<string>();
  const add = (v: string | null) => { if (v) seen.add(v); };
  for (const g of grids) {
    for (const { row, children } of groupsOf(g.grid.rows)) {
      if (!groupMatches(row, others)) continue;
      if (row.grain === "marketplace") {
        if (lineMatches(row, others, false)) add(fieldOf(row, field));
        continue;
      }
      const matching = children.filter((child) => lineMatches(child, others, false));
      if (lineFilter && matching.length === 0) continue;
      if (LINE_FIELDS.includes(field)) for (const child of lineFilter ? matching : children) add(fieldOf(child, field));
      else add(fieldOf(row, field));
    }
  }
  add(filters[field] ?? null);
  return [...seen].sort().map((value) => ({ value, label: value }));
}

/**
 * Every filter with its value kept only if the loaded rows still offer it. A value left over from another
 * region reads as "all" instead of silently emptying the table. All six keys are always present, null when
 * unset, so a FilterBar change also clears any stale key from the URL. Each value is judged on its own against every
 * loaded row, never against the other filters: a pair no row holds stays, and the table says no row matches.
 */
export function cleanFilters(grids: GridResponse[], raw: FilterValues): Filters {
  const out = {} as Filters;
  for (const field of FILTER_FIELDS) {
    const value = typeof raw[field] === "string" ? (raw[field] as string) : null;
    out[field] = value && options(grids, field).some((o) => o.value === value) ? value : null;
  }
  return out;
}

/**
 * The URL after a FilterBar change. The current search is the base, so a filter the person cleared is
 * removed rather than surviving a merge, and the page resets to the first.
 */
export function searchForFilters(search: Record<string, unknown>, next: FilterValues): Record<string, unknown> {
  return { ...applyFilters(search, next), pp_page: undefined };
}

function groupMatches(row: GridRow, filters: Filters): boolean {
  return GROUP_FIELDS.every((field) => !filters[field] || fieldOf(row, field) === filters[field]);
}
function lineMatches(row: GridRow, filters: Filters, onlyMissing: boolean): boolean {
  if (filters.location && fieldOf(row, "location") !== filters.location) return false;
  if (filters.marketplace && fieldOf(row, "marketplace") !== filters.marketplace) return false;
  return !onlyMissing || row.missing.length > 0;
}

export type VisibleRow = { row: GridRow; market: Market; child: boolean; childCount: number; expanded: boolean };

export function keyMatches(row: GridRow, key: string): boolean {
  const exact = rowKey(row);
  return exact === key || (key.split("|").length === 4 && exact.split("|").slice(0, 4).join("|") === key);
}

/**
 * The visible rows as groups: an Amazon marketplace row alone, or a Shopify store row followed by its
 * ship-country rows when the store is open or a line filter is active. Object order is kept.
 * A store row with its own missing reason stays visible under "only rows with something missing" even
 * when none of its countries is missing anything.
 */
export function visibleGroups(
  grids: GridResponse[], filters: Filters, onlyMissing: boolean, openStores: Set<string>,
): VisibleRow[][] {
  const lineFilter = Boolean(filters.location || filters.marketplace || onlyMissing);
  const placeFilter = Boolean(filters.location || filters.marketplace);
  const groups: VisibleRow[][] = [];
  for (const g of grids) {
    const market = g.grid.market;
    for (const { row, children: all } of groupsOf(g.grid.rows)) {
      if (!groupMatches(row, filters)) continue;
      if (row.grain === "marketplace") {
        if (lineMatches(row, filters, onlyMissing)) groups.push([{ row, market, child: false, childCount: 0, expanded: false }]);
        continue;
      }
      const matching = all.filter((child) => lineMatches(child, filters, onlyMissing));
      const selfMissing = onlyMissing && !placeFilter && row.missing.length > 0;
      if (lineFilter && matching.length === 0 && !selfMissing) continue;
      const shown = [...openStores].some((key) => keyMatches(row, key)) ? (lineFilter ? matching : all) : lineFilter ? matching : [];
      groups.push([
        { row, market, child: false, childCount: all.length, expanded: shown.length > 0 },
        ...shown.map((child) => ({ row: child, market, child: true, childCount: 0, expanded: false })),
      ]);
    }
  }
  return groups;
}

// The one numeric read in this file: it orders the rows (layout), never a figure shown as fact; the cells show C2's strings.
const num = (value: string | null | undefined): number | null => (value == null ? null : Number(value));
/** The margins a row is sorted and flagged by: the grid's, or in an order-type view the view's at the net (C1 v1.23), null
 * when the view has none for the row. */
export type MarginsOf = (v: VisibleRow) => Pick<Margins, "gross_pct" | "fulfilment_pct"> | null;
const gridMargins: MarginsOf = (v) => v.row.margins;
/** A group's rank under one sort, from its first row (the store or marketplace row; its countries follow it). Null last. */
function compare(a: VisibleRow[], b: VisibleRow[], sort: SortKey, marginsOf: MarginsOf): number {
  const x = a[0].row, y = b[0].row;
  if (sort === "product") return x.product.localeCompare(y.product, "en-GB") || x.variant.localeCompare(y.variant, "en-GB");
  const mx = marginsOf(a[0]), my = marginsOf(b[0]);
  const [p, q] = sort === "gross_low" ? [num(mx?.gross_pct), num(my?.gross_pct)]
    : sort === "fulfil_high" ? [num(mx?.fulfilment_pct), num(my?.fulfilment_pct)] : [num(x.price?.value), num(y.price?.value)];
  if (p == null) return q == null ? 0 : 1;
  if (q == null) return -1;
  return sort === "gross_low" ? p - q : q - p;
}
/** The groups in the chosen order (C1 v1.17, owner ruling Q15), or as the grid wrote them with none. The order is taken within
 * one market, and for a price within one currency, in first-seen order of each: a figure is never compared across currencies.
 * `marginsOf` is the view's figures in an order-type view (C1 v1.23), the grid's by default; a price sorts by the list price. */
export function sortGroups(groups: VisibleRow[][], sort: SortKey | null, marginsOf: MarginsOf = gridMargins): VisibleRow[][] {
  if (!sort) return groups;
  const runs = new Map<string, VisibleRow[][]>();
  for (const g of groups) {
    const key = sort === "price_high" ? `${g[0].market}|${g[0].row.price?.currency ?? g[0].row.currency ?? ""}` : g[0].market;
    runs.set(key, [...(runs.get(key) ?? []), g]);
  }
  return [...runs.values()].flatMap((run) => [...run].sort((a, b) => compare(a, b, sort, marginsOf)));
}

/** One page of groups, so a store and its countries never split across pages. The page index is clamped. */
export function pageOf(groups: VisibleRow[][], requested: number): { pageIndex: number; rows: VisibleRow[] } {
  const last = Math.max(0, Math.ceil(groups.length / PAGE_SIZE) - 1);
  const pageIndex = Math.min(Math.max(0, requested), last);
  return { pageIndex, rows: groups.slice(pageIndex * PAGE_SIZE, (pageIndex + 1) * PAGE_SIZE).flat() };
}

export function findRow(grids: GridResponse[], key: string): { row: GridRow; market: Market } | null {
  for (const g of grids) for (const row of g.grid.rows) if (rowKey(row) === key) return { row, market: g.grid.market };
  if (key.split("|").length === 4) {
    for (const g of grids) for (const row of g.grid.rows) if (keyMatches(row, key)) return { row, market: g.grid.market };
  }
  return null;
}

export function focusKeyFor(grids: GridResponse[], key: string): string {
  const found = findRow(grids, key);
  return found ? rowKey(found.row) : key;
}

/** Siblings for the detail sheet: same-SKU marketplace rows (Amazon) or the ship-country rows of the parent (Shopify). */
export function siblings(grids: GridResponse[], row: GridRow, market: Market): GridRow[] {
  const g = grids.find((x) => x.grid.market === market);
  if (!g) return [];
  if (row.channel === "amazon") return g.grid.rows.filter((r) => r.grain === "marketplace" && r.xero_sku === row.xero_sku);
  const parent = row.grain === "store" ? row.xero_sku : row.parent_sku;
  return g.grid.rows.filter((r) => r.grain === "ship_country" && r.parent_sku === parent);
}
